import {
  type KeyboardEvent,
  type MutableRefObject,
  type Ref,
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export function composeRefs<T>(
  ...refs: Array<Ref<T> | undefined>
): RefCallback<T> {
  return (element: T | null) => {
    refs.forEach(ref => {
      if (ref === null || ref === undefined) return;
      if (typeof ref === "function") {
        ref(element);
      } else {
        (ref as MutableRefObject<T | null>).current = element;
      }
    });
  };
}

interface Options<T> {
  items: T[];
  isActive: boolean;
  onSelect: (item: T) => void;
  onEscape?: () => void;
}

interface Result {
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  registerItem: (
    index: number,
  ) => (element: HTMLElement | HTMLButtonElement | null) => void;
}

export function useListKeyboardNav<T>({
  items,
  isActive,
  onSelect,
  onEscape,
}: Options<T>): Result {
  const [highlightedIndex, setHighlightedIndex] = useState<number>(
    items.length > 0 ? 0 : -1,
  );
  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    if (!isActive) return;
    setHighlightedIndex(items.length > 0 ? 0 : -1);
  }, [isActive]);

  useEffect(() => {
    if (items.length === 0) {
      if (highlightedIndex !== -1) setHighlightedIndex(-1);
      return;
    }
    if (highlightedIndex < 0 || highlightedIndex >= items.length) {
      setHighlightedIndex(0);
    }
  }, [items.length]);

  useEffect(() => {
    if (!isActive || highlightedIndex < 0) return;
    const element = itemRefs.current[highlightedIndex];
    element?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, isActive]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      if (!isActive) return;

      switch (event.key) {
        case "ArrowDown":
          if (items.length === 0) return;
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev < 0 ? 0 : (prev + 1) % items.length,
          );
          break;
        case "ArrowUp":
          if (items.length === 0) return;
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev < 0
              ? items.length - 1
              : (prev - 1 + items.length) % items.length,
          );
          break;
        case "Home":
          if (items.length === 0) return;
          event.preventDefault();
          setHighlightedIndex(0);
          break;
        case "End":
          if (items.length === 0) return;
          event.preventDefault();
          setHighlightedIndex(items.length - 1);
          break;
        case "Enter":
          if (
            items.length > 0 &&
            highlightedIndex >= 0 &&
            highlightedIndex < items.length
          ) {
            event.preventDefault();
            onSelect(items[highlightedIndex]);
          }
          break;
        case "Escape":
          if (onEscape !== undefined) {
            event.preventDefault();
            onEscape();
          }
          break;
      }
    },
    [items, isActive, onSelect, onEscape, highlightedIndex],
  );

  const registerItem = useCallback(
    (index: number) =>
      (element: HTMLElement | HTMLButtonElement | null): void => {
        itemRefs.current[index] = element;
      },
    [],
  );

  return {
    highlightedIndex,
    setHighlightedIndex,
    handleKeyDown,
    registerItem,
  };
}
